import type { AgentTaskModel } from "@fencyai/js";
import { useAgentTasks } from "@fencyai/react";
import { useCallback, useRef } from "react";
import { useRumError } from "../errors/RumComponentContext/useRumError";

type FencyMessage = { role: "SYSTEM" | "USER" | "ASSISTANT"; content: string };

export type StructuredCompletionRequest = {
  model: AgentTaskModel;
  jsonSchema: string;
  messages: FencyMessage[];
};

type UseStructuredCompletionProps = {
  /** Tag for the Datadog error captured when a completion fails, e.g. "cpv-suggestions". */
  feature: string;
  fetchClientToken: () => Promise<{ clientToken: string }>;
  timeoutMs?: number;
};

const DEFAULT_TIMEOUT_MS = 60_000;

/**
 * Resolves the `jsonResponse` of a Fency StructuredChatCompletion, or throws on any failure.
 *
 * @fencyai/react@0.1.160 settles createAgentTask only on a "Completed" event; a task "Error" (or
 * stream error) otherwise leaves the promise pending forever. Each in-flight call registers its
 * reject in `pending`, so an error event fails it the instant it arrives (with the real Fency
 * message); a timeout backstops a stream that dies without any terminal event.
 */
export const useStructuredCompletion = ({
  feature,
  fetchClientToken,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UseStructuredCompletionProps) => {
  const rumError = useRumError();
  const pending = useRef(new Map<symbol, (reason: Error) => void>());

  const failAll = (error: Error) => {
    pending.current.forEach((reject) => reject(error));
    pending.current.clear();
  };

  const fetchToken = useCallback(
    async () => fetchClientToken(),
    [fetchClientToken],
  );

  const { createAgentTask } = useAgentTasks({
    fetchCreateAgentTaskClientToken: fetchToken,
    onAgentTaskProgressItemUpdated: ({ data }) => {
      if (data.taskType === "StructuredChatCompletion" && data.eventType === "Error") {
        failAll(new Error(data.message));
      }
    },
    onStreamError: ({ error }) => failAll(new Error(error.message)),
  });

  return useCallback(
    async (request: StructuredCompletionRequest, logContext?: Record<string, unknown>): Promise<string> => {
      const callId = Symbol();
      const timeout = setTimeout(
        () => pending.current.get(callId)?.(new Error("Structured completion timed out")),
        timeoutMs,
      );
      try {
        const result = await Promise.race([
          createAgentTask({ type: "StructuredChatCompletion", ...request }),
          new Promise<never>((_, reject) => pending.current.set(callId, reject)),
        ]);
        if (result.type !== "success") throw new Error(result.error.message);
        if (result.response.taskType !== "StructuredChatCompletion") {
          throw new Error("Unexpected structured completion response.");
        }
        return result.response.response.jsonResponse;
      } catch (e) {
        rumError(e, { ...logContext, feature });
        throw e;
      } finally {
        pending.current.delete(callId);
        clearTimeout(timeout);
      }
    },
    [createAgentTask, feature, rumError, timeoutMs],
  );
};
